import { LatLon } from '../../util/geodesy/latlon';
import { parseLatLon } from '../../airport/util/parseUtils';
import { Address } from '../../shared/models/address.model';
import { BusinessContact } from './business-contact.model';
import { BusinessFuel } from './business-fuel.model';
import { BusinessHours } from './business-hours.model';
import { BusinessService } from './business-service.model';
import { BusinessOverviewFee } from './business-overview-fee.model';
import { BusinessAdmin } from './business-admin.model';
import { BusinessTag } from './business-tag.model';
import { FlightSchool } from './flight-school.model';
import { FlyingClub } from './flying-club.model';
import { BusinessReview } from './business-review.model';
import { ReviewSummary } from '../../shared/models/review-summary.model';
import { AirportSummary } from '../../airport/models/airport-summary.model';
import { Webcam } from './webcam.model';


/**
 * Business model. A business may be a regular FBO, flying club, college/university, or possibly a combination of
 * multiple types. A business may provide fuel, line services, flight training, and other services. Typically,
 * businesses are stationed at and associated with a single airport, but this is not necessarily always the case.
 */
export class Business {
  /**
   * Unique identifier of this business record in the database
   */
  id: number;

  /**
   * Source of data FAA = 1, AOPA = 2, Admin = 3, Member = 4, AptMgr = 5, BusMgr = 6, CusSrv = 7, EntPart = 8, Low Lead 100 = 9
   */
  sourceId: number;

  /**
   * Normal business hours for business
   */
  hoursText: string;

  /**
   * Number of a&p mechanics on staff
   */
  apMech: number;

  /**
   * Business name
   */
  name: string;

  /**
   * Alternate name for the business ("do business as"), displayed next to the name
   */
  doBusAs: string;

  /**
   * Primary business address (displayed to user)
   */
  address: Address;

  /**
   * Mailing address (if different from primary business address; only returned via API for admins)
   */
  mailingAddress: Address;

  /**
   * Primary business phone (displayed to user)
   */
  phone: string;

  /**
   * Fax number (displayed to user)
   */
  fax: string;

  /**
   * Primary email address (displayed to user)
   */
  email: string;

  /**
   * Business web site URL
   */
  webAddress: string;

  /**
   * Business ARINC frequency (if available)
   */
  arincFreq: string;

  /**
   * Business UNICOM frequency (if available)
   */
  unicomFreq: string;

  /**
   * Location of business on field (as an intercardinal or cardinal direction, e.g. "SW", "N")
   */
  locationOnField: string;

  /**
   * Type of facility
   */
  facilityType: string;

  /**
   * Abreviations of business
   */
  name32: string;

  /**
   * Location description (free-form text)
   */
  locationDesc: string;

  /**
   * Location description (free-form text)
   */
  closedHolidays: string;

  /**
   * Location of the business as a lat/lon
   */
  location: LatLon;

  /**
   * Start year that the business began operating
   */
  inBusinessSince: number;

  /**
   * Free-form text submitted by business manager to describe the business
   */
  description: string;

  /**
   * Business contacts
   */
  contacts: Array<BusinessContact>;

  /**
   * Business fuel information
   */
  fuel: Array<BusinessFuel>;

  /**
   * Business hours
   */
  hours: Array<BusinessHours>;

  /**
   * Business fees/services
   */
  services: Array<BusinessService>;

  /**
   * Business overview fees
   */
  overviewFees: Array<BusinessOverviewFee>;

  /**
   * Business tags
   */
  tags: Array<BusinessTag>;

  /**
   * If business is a flight school, this field stores additional information about the flight school (number of
   * instructors, etc.).
   */
  flightSchool: FlightSchool;

  /**
   * If the business is affiliated with a college/university, this field hold the name of the college/university.
   */
  collegeName: string;

  /**
   * If business is a flying club, this field stores additional information about the flying club (number of
   * members, aircraft, etc.).
   */
  flyingClub: FlyingClub;

  /**
   * Business reviews/ratings
   */
  reviews: Array<BusinessReview>;

  /**
   * Summary object of reviews for this entity
   */
  reviewSummary: ReviewSummary;

  /**
   * List of airports with which this business is associated (if any)
   */
  airports: Array<AirportSummary>;

  /**
   * Date of latest fee update if the business has reported fees
   */
  feesLastUpdate: Date;

  /**
   * String containing the exception days to the business hours each year
   * (for example, "major holidays")
   */
  hoursExceptions: string;

  /**
   * Business admins
   */
  admins: Array<BusinessAdmin>;

  /**
   * Business webcams
   */
  webcams: Webcam[];

  /**
   * Current FTE Award Year
   */
  fteAwardYear: string;

  /**
   * Parses a business as returned by the API into an instance of the Business class
   */
  static parse(data: any): Business {
    return Object.assign(new Business(), {
      id: data.businessId,
      name: data.name,
      sourceId: data.sourceId,
      doBusAs: data.doBusAs,
      address: Business._getAddress(data, 'business'),
      mailingAddress: Business._getAddress(data, 'mailing'),
      phone: data.phone1 ? (data.phone1 + (data.ext1 ? 'x' + data.ext1 : '')) : null,
      fax: data.fax,
      hoursText: data.hours,
      apMech: data.apMech,
      email: data.emailAddress,
      webAddress: data.webAddress,
      arincFreq: data.arincFreq,
      unicomFreq: data.unicomFreq,
      locationOnField: data.locationOnField,
      facilityType: data.facilityType,
      name32: data.name32,
      locationDesc: data.locationDesc,
      closedHolidays: data.closedHolidays,
      location: data.location ? parseLatLon(data.location.latitude, data.location.longitude) : null,
      inBusinessSince: data.inBusinessSince,
      description: data.description,
      contacts: (data.businessContacts || []).map(d => BusinessContact.parse(d)),
      fuel: (data.businessFuels || []).map(d => BusinessFuel.parse(d)),
      hours: (data.businessHours || []).map(d => BusinessHours.parse(d)),
      services: (data.businessServices || []).map(d => BusinessService.parse(d)),
      feesLastUpdate: data.feesLastUpdate,
      hoursExceptions: data.closedHolidays,
      admins: (data.businessAdmins || []).map(d => BusinessAdmin.parse(d)),
      overviewFees: (data.overviewFees || []).map(d => BusinessOverviewFee.parse(d)),
      tags: (data.businessTags || []).map(d => BusinessTag.parse(d)),
      flightSchool: data.businessFlightSchool ? FlightSchool.parse(data.businessFlightSchool) : null,
      collegeName: data.collegeName,
      flyingClub: data.businessFlyingClub ? FlyingClub.parse(data.businessFlyingClub) : new FlyingClub(),
      reviews: (data.businessReviews || []).map(d => BusinessReview.parse(d)),
      reviewSummary: data.reviewSummary,
      airports: (data.airports || []).map(d => AirportSummary.parse(d)),
      webcams: (data.webcams || []).map(d => Webcam.parse(d)),
      fteAwardYear: data.fteAwardYear
    });
  }

  /**
   * Parses address from API
   * @param {any} data Business data as returned by API
   * @param {string} type Address type ('business' or 'mailing')
   * @returns {Address}
   */
  private static _getAddress(data: any, type: string): Address {
    const addressData = data.addresses;
    if (!addressData) {
      return new Address();
    }
    const address = addressData.find(addr => addr.type === type);
    if (!address) {
      return new Address();
    }
    return Address.parse(address.addr1, address.addr2, address.city, address.state, address.zip, address.country) || new Address();
  }

  /**
   * Serializes Business Obj into JSON
   * @returns {JSON}
   */
  public serialize (): any {
    return {
      name: this.name,
      addresses: this.serializeAddresses(),
      phone1: this.phone,
      fax: this.fax,
      hours: this.hoursText,
      apMech: this.apMech,
      arincFreq: this.arincFreq,
      unicomFreq: this.unicomFreq,
      locationOnField: this.locationOnField,
      emailAddress: this.email,
      webAddress: this.webAddress,
      facilityType: this.facilityType,
      doBusAs: this.doBusAs,
      name32: this.name32,
      locationDesc: this.locationDesc,
      closedHolidays: this.closedHolidays,
      inBusinessSince: this.inBusinessSince,
      description: this.description,
      collegeName: this.collegeName,
      location: this.serializeLocation(),
      businessFlyingClub: this.serializeClub(),
      flightSchool: this.serializeFlightSchool()
    };
  }

  protected serializeAddresses (): any {
    let addresses: any;
    if (this.address) {
      addresses = [{
        type: 'business',
        addr1: this.address.street1,
        addr2: this.address.street2,
        city: this.address.city,
        state: this.address.stateProvince,
        zip: this.address.postalCode,
        country: this.address.country
      }];
    } else {
      addresses = [{
        type: 'business',
        addr1: null,
        addr2: null,
        city: null,
        state: null,
        zip: null,
        country: 'US'
      }];
    }
    if (this.mailingAddress) {
      addresses.push({
        type: 'mailing',
        addr1: this.mailingAddress.street1,
        addr2: this.mailingAddress.street2,
        city: this.mailingAddress.city,
        state: this.mailingAddress.stateProvince,
        zip: this.mailingAddress.postalCode,
        country: this.mailingAddress.country
      });
    } else {
      addresses.push({
        type: 'mailing',
        addr1: null,
        addr2: null,
        city: null,
        state: null,
        zip: null,
        country: null
      });
    }
    return addresses;
  }

  protected serializeLocation (): any {
    let location: any;
    if (this.location) {
      location = {
        latitude: this.location.lat,
        longitude: this.location.lon,
      };
    } else {
      location = {
        latitude: null,
        longitude: null,
      };
    }
    return location;
  }

  private serializeClub() {
    return this.flyingClub ? {
      clubMembers: this.flyingClub.clubMembers,
      clubAircraft: this.flyingClub.clubAircraft,
    } : null;
  }

  public serializeFlightSchool() {
    return this.flightSchool ? {
      businessFlightSchool: {
        instructorFull: this.flightSchool.cfiCountFull || this.flightSchool.cfiCountFull === 0 ? this.flightSchool.cfiCountFull : null,
        instructorPart: this.flightSchool.cfiCountPart || this.flightSchool.cfiCountPart === 0 ? this.flightSchool.cfiCountPart : null,
        cfiCountContract: this.flightSchool.cfiCountContract || this.flightSchool.cfiCountContract === 0 ?
          this.flightSchool.cfiCountContract : null
      }
    } : null;
  }

}
